import {
    closest,
    Component,
    ContentObserverService,
    EASE_LINEAR,
    element as $,
    elements as $$,
    ITweezer,
    supportsPassiveEvents,
    SCREEN_LARGE,
    SCREEN_XXLARGE,
    SCREEN_MEDIUM,
} from '@contorion/core';

const CLASS_NO_BUTTONS_DEFAULT: string = 'slider--no-buttons';
const CLASS_ITEM_LEFT_DEFAULT: string = 'slider-item__left';
const CLASS_ITEM_RIGHT_DEFAULT: string = 'slider-item__right';
const CLASS_ACTIVE: string = 'active';
const CLASS_MAIN_PRODUCT_IMAGE: string = 'js-main-product-image';
const CLASS_HEADER_SLIDER_CONTENT: string = 'js-header-slider-content';
const CLASS_CAMPAIGN_SLIDER_CONTENT: string = 'js-campaign-slider-content';
const DIRECTION_RIGHT: string = 'right';
const DIRECTION_LEFT: string = 'left';
const SELECTOR_BUTTON: string = '.js-slider-button';
const SELECTOR_BUTTON_ACTIVE: string = '.js-slider-button-active';
const SELECTOR_CONTENT: string = '.js-slider-content';
const SELECTOR_BUTTON_LEFT: string = '.js-slider-left';
const SAME_WIDTH_WITH_ARROW_BUTTON: string = '.js-same-width-with-arrow-button';
const SELECTOR_BUTTON_RIGHT: string = '.js-slider-right';
const SELECTOR_SHOPPING_LIST_WIDGET: string = '.js-shopping-list-widget';
const SELECTOR_ADD_TO_CART_WIDGET: string = '.js-add-to-cart';
const SELECTOR_SLIDER_INDICATOR: string = '.js-slider-indicator';
const SELECTOR_SLIDER_CLICK_INDICATOR: string = '.js-indicator-click';
const SELECTOR_HERO_SLIDER_TEXT_INDICATOR: string = '.js-text-slider-indicator';
const SELECTOR_HERO_SLIDER_CIRCLE_INDICATOR: string = '.js-circle-slider-indicator';
const SELECTOR_HERO_SLIDER_CIRCLE_INDICATOR_CONTAINER: string = '.js-circle-slider-indicator-container';
const SELECTOR_HERO_NAVIGATION_ELEMENTS: string = '.js-hero-navigation-elements';
const SELECTOR_HERO_CAROUSEL_NAVIGATION_CONTAINER: string = '.js-hero-carousel--navigation-container';

export class SliderComponent extends Component {
    protected tweezer: ITweezer;

    protected tweezerJs: any;

    protected lastScrollLeft: number;

    protected direction: string;

    protected timeout: number;

    protected autoTimeout: number;

    protected autoDesktopTimeout: number;

    protected prevSameWidthWithArrowButtonEleWidth: number = 0;

    protected autoEnabled: boolean = true;

    protected touchStarted: boolean = false;

    protected touchStartX: number;

    protected scrollableStartLeft: number;

    protected hadInteractionWithSlider: boolean = false;

    protected get shouldSupportTouchSwipe(): boolean {
        return !!this.element.dataset.touchSwipeable;
    }

    protected get isHeroCarousel(): boolean {
        return !!this.element.dataset.heroCarousel;
    }

    //added for transitioning from old slider to new.
    //the following three getters can be removed if we only have one slider.
    protected get leftButtonClass(): string {
        return this.element.dataset.classLeftButton ?
            this.element.dataset.classLeftButton : CLASS_ITEM_LEFT_DEFAULT;
    }

    protected get rightButtonClass(): string {
        return this.element.dataset.classRightButton ?
            this.element.dataset.classRightButton : CLASS_ITEM_RIGHT_DEFAULT;
    }

    protected get noButtonClass(): string {
        return this.element.dataset.classNoButton ?
            this.element.dataset.classNoButton : CLASS_NO_BUTTONS_DEFAULT;
    }

    protected get maxLeft(): number {
        return this.scrollable.scrollWidth - this.scrollable.offsetWidth;
    }

    protected get scrollable(): HTMLElement {
        return <HTMLElement> $(SELECTOR_CONTENT, this.element);
    }

    protected get scrollables(): Array<HTMLElement> {
        return $$<HTMLElement>(SELECTOR_CONTENT);
    }

    protected get clickIndicators(): Array<HTMLElement> {
        return $$<HTMLElement>(SELECTOR_SLIDER_CLICK_INDICATOR, this.element);
    }

    protected get buttonLeft(): HTMLButtonElement {
        return <HTMLButtonElement> $(SELECTOR_BUTTON_LEFT, this.element);
    }

    protected get sameWidthWithArrowButtonEles(): Array<HTMLElement> {
        return $$<HTMLButtonElement>(SAME_WIDTH_WITH_ARROW_BUTTON, this.element);
    }

    protected get buttonRight(): HTMLButtonElement {
        return <HTMLButtonElement> $(SELECTOR_BUTTON_RIGHT, this.element);
    }

    protected get sliderButtons(): Array<HTMLButtonElement> {
        return $$<HTMLButtonElement>(SELECTOR_BUTTON);
    }

    protected get buttonActive(): HTMLElement {
        return <HTMLElement> $(SELECTOR_BUTTON_ACTIVE, this.element);
    }

    protected get sliderIndicators(): Array<HTMLElement> {
        return $$<HTMLElement>(SELECTOR_SLIDER_INDICATOR);
    }

    protected get heroSliderCircleIndicatorContainer(): HTMLElement {
        return <HTMLElement> $(SELECTOR_HERO_SLIDER_CIRCLE_INDICATOR_CONTAINER, this.element);
    }

    protected get heroSliderTextIndicators(): Array<HTMLElement> {
        return $$<HTMLElement>(SELECTOR_HERO_SLIDER_TEXT_INDICATOR, this.element);
    }

    protected get heroSliderCircleIndicators(): Array<HTMLElement> {
        return $$<HTMLElement>(SELECTOR_HERO_SLIDER_CIRCLE_INDICATOR, this.element);
    }

    protected get navigationElements(): Array<HTMLElement> {
        return $$<HTMLElement>(SELECTOR_HERO_NAVIGATION_ELEMENTS, this.element);
    }

    protected get heroCarouselNavContainer(): HTMLElement {
        return <HTMLElement> $(SELECTOR_HERO_CAROUSEL_NAVIGATION_CONTAINER, this.element);
    }

    protected get preventTapToSlide(): boolean {
        return this.element.dataset.preventTapToSlide !== undefined;
    }

    protected get autoTime(): number {
        const { auto } = this.element.dataset;

        if (typeof auto === 'undefined') {
            return 0;
        }

        return parseInt(auto, 10);
    }

    // this auto time is for desktop automation
    protected get autoDesktopTime(): number {
        const { autoDesktopTime } = this.element.dataset;
        if (typeof autoDesktopTime === 'undefined') {
            return 0;
        }

        return parseInt(autoDesktopTime, 10);
    }
    protected get slidersPerView(): number {
        const { multiSlidersPerView } = this.element.dataset;
        if (!multiSlidersPerView) {
            return 1;
        }
        let perView = 1;
        if (document.body.offsetWidth >= SCREEN_MEDIUM.minWidth) {
            perView = 2;
        }
        if (document.body.offsetWidth >= SCREEN_XXLARGE.minWidth) {
            perView = 3;
        }

        return perView;
    }

    protected get sliders(): Array<HTMLElement> {
        return [].concat.apply([], this.scrollable.children);
    }

    protected get slideWidth(): number {
        return this.scrollable.offsetWidth +
            parseInt(window.getComputedStyle(this.sliders[0]).marginRight, 10);
    }

    protected get slideMoveMaxWidth(): number {
        return this.sliders[this.sliders.length - 1].offsetLeft
            + this.sliders[this.sliders.length - 1].offsetWidth
            + parseInt(window.getComputedStyle(this.sliders[0]).marginRight, 10)
            - this.slideWidth;
    }

    constructor(protected element: HTMLElement) {
        super(element);

        if (this.shouldSupportTouchSwipe && !this.isHeroCarousel) {
            this.onTouchStart = this.onTouchStart.bind(this);
            this.onTouchMove = this.onTouchMove.bind(this);
            this.onTouchEnd = this.onTouchEnd.bind(this);
        }
        this.onScroll = this.onScroll.bind(this);
        this.onMouseEnter = this.onMouseEnter.bind(this);
        this.onMouseLeave = this.onMouseLeave.bind(this);
        this.onButtonLeftClick = this.onButtonLeftClick.bind(this);
        this.onButtonRightClick = this.onButtonRightClick.bind(this);
        this.autoNext = this.autoNext.bind(this);
        this.bindEvents = this.bindEvents.bind(this);
        this.toggleButtons = this.toggleButtons.bind(this);
        this.onAfterScroll = this.onAfterScroll.bind(this);
        this.startAutoOnDesktop = this.startAutoOnDesktop.bind(this);
        this.stopAutoOnDesktop = this.stopAutoOnDesktop.bind(this);
        this.setInteractionState = this.setInteractionState.bind(this);
        this.adjustIndicatorItems();
        this.startAutoOnDesktop();

        this.scrollables.forEach((scrollable) => {
            if (!this.hasSliderContent(scrollable)) {
                this.waitForContent();

                const config: MutationObserverInit = {
                    attributes: true,
                    characterData: true,
                    childList: true,
                };
                const observer: MutationObserver = new MutationObserver((mutations) => {
                    mutations.forEach(() => {
                        this.init();
                    });
                });

                observer.observe(this.scrollable, config);

            } else {
                this.init();
            }
        });
    }

    protected hideNavigationElements = () => {
        [...this.navigationElements].forEach((ele) => {
            ele.style.display = 'none';
        });
    }

    protected bindDotClickEvent = () => {
        this.clickIndicators.forEach((clickIndicator: HTMLElement) => {
            clickIndicator.addEventListener('click', () => {
                const idx = Number(clickIndicator.dataset.idx);
                if (!clickIndicator.classList.contains(CLASS_ACTIVE)) {
                    this.moveToSlide(idx);
                }
            });
        });
    }

    protected adjustIndicatorItems = async() => {
        if (!this.hasSliderContent(this.scrollable)) {
            await this.waitForContent();
        }

        if (this.shouldSupportTouchSwipe &&
            this.isHeroCarousel &&
            this.sliders.length > this.slidersPerView) {
            this.initSwipeGensture();
        } else {
            this.hideNavigationElements();
        }

        const reCalculateIndicator = () => {
            const slides = Math.ceil(this.scrollable.children.length / this.slidersPerView);
            // @ts-ignore
            const currentAcitveIdx = [...this.heroSliderCircleIndicatorContainer.children]
                .findIndex((child) => child.classList.contains(CLASS_ACTIVE));
            const dotItem = this.heroSliderCircleIndicatorContainer.children.item(0);
            dotItem.classList.remove(CLASS_ACTIVE);
            const dotItems = Array(slides).fill(0).map(() => dotItem.cloneNode(true));
            this.heroSliderCircleIndicatorContainer.innerHTML = '';
            this.heroSliderCircleIndicatorContainer.append(...dotItems);
            this.heroSliderCircleIndicatorContainer
                .children[currentAcitveIdx].classList.add(CLASS_ACTIVE);
            // @ts-ignore
            [...this.heroSliderCircleIndicatorContainer.children].forEach((child, index) => {
                child.dataset.idx = index;
            });
            this.bindDotClickEvent();
        };
        if (this.heroSliderCircleIndicators.length > 0) {
            reCalculateIndicator();
        }
    }

    protected async waitForContent(): Promise<void> {
        const observer: ContentObserverService = new ContentObserverService();
        await observer.observe(this.scrollable);
        this.init();
    }

    protected async init(): Promise<void> {
        this.tweezerJs = await import (/* webpackChunkName: "tweezer.js" */ 'tweezer.js');
        this.scrollable.scrollLeft = 0;
        this.bindEvents(true);
        this.updateProximityClasses(0);

        if (this.autoTime > 0) {
            this.autoTimeout = window.setInterval(this.autoNext, this.autoTime);
        }

        if (this.buttonActive) {
            this.scrollToElement(this.buttonActive);
        }
    }

    protected bindEvents(onInit?: boolean): void {
        if (this.shouldSupportTouchSwipe && !this.isHeroCarousel) {
            this.scrollable.addEventListener('scroll', this.onScroll);
            this.scrollable.addEventListener(
                'touchstart',
                this.onTouchStart,
                supportsPassiveEvents ? { passive: true } : false
            );
        }
        this.scrollable.addEventListener('mouseenter', this.onMouseEnter);
        this.scrollable.addEventListener('mouseleave', this.onMouseLeave);
        this.element.addEventListener('mouseenter', this.stopAutoOnDesktop);
        this.element.addEventListener('mouseleave', this.startAutoOnDesktop);
        this.element.addEventListener('click', this.setInteractionState);
        this.buttonLeft?.addEventListener('click', this.onButtonLeftClick);
        this.buttonRight?.addEventListener('click', this.onButtonRightClick);

        if (onInit) {
            this.sliderButtons.forEach((button) => {
                button.addEventListener('click', () => {
                    this.scrollToElement(button);
                });
            });
        }

        window.setTimeout(this.toggleButtons, 500);
    }

    protected unbindEvents(): void {
        this.scrollable.removeEventListener('scroll', this.onScroll);
        this.scrollable.removeEventListener('touchstart', this.onTouchStart);
        this.scrollable.removeEventListener('mouseenter', this.onMouseEnter);
        this.scrollable.removeEventListener('mouseleave', this.onMouseLeave);
        this.element.removeEventListener('mouseenter', this.stopAutoOnDesktop);
        this.element.removeEventListener('mouseleave', this.startAutoOnDesktop);
        this.element.removeEventListener('click', this.setInteractionState);
        this.buttonLeft?.removeEventListener('click', this.onButtonLeftClick);
        this.buttonRight?.removeEventListener('click', this.onButtonRightClick);
    }

    protected toggleButtons(): void {
        if (this.buttonLeft) {
            this.buttonLeft.disabled = this.scrollable.scrollLeft <= 0;
        }

        if (this.buttonRight && this.scrollable.scrollLeft != 0) {
            this.buttonRight.disabled = this.scrollable.scrollLeft + 1 >= this.maxLeft;
        }

        if (
            this.buttonLeft
            && this.buttonRight
            && this.buttonLeft.disabled
            && this.buttonRight.disabled
        ) {
            this.element.classList.add(this.noButtonClass);
        } else {
            this.element.classList.remove(this.noButtonClass);
        }
    }

    protected scrollTo(end: number): void {
        const start: number = this.scrollable.scrollLeft;
        this.unbindEvents();

        this.tweezer = new this.tweezerJs.default({
            duration: 300,
            easing: EASE_LINEAR,
            end,
            start,
        })
            .on('tick', (scrollLeft: number) => {
                this.scrollable.scrollLeft = scrollLeft;
            })
            .on('done', () => {
                window.setTimeout(this.bindEvents, 300);
            })
            .begin();
        this.updateProximityClasses(end);
    }

    protected updateProximityClasses(startOffset: number): void {
        const end = startOffset + this.scrollable.offsetWidth,
            midpoint = Math.floor(startOffset + (this.scrollable.offsetWidth / 2)),
            children: Array<HTMLElement> = [].concat.apply([], this.scrollable.children);
        let itemClass: string;
        let activeItemIndex: number;
        for (const child of children) {
            child.classList.remove(this.leftButtonClass);
            if (child.offsetLeft >= startOffset && child.offsetLeft < end) {
                itemClass = child.offsetLeft < midpoint ?
                    this.leftButtonClass : this.rightButtonClass;
                child.classList.add(itemClass);

                // different kinds of indicators are for different version's slider
                const allTypeSliderIndicators = [
                    this.sliderIndicators,
                    this.heroSliderTextIndicators,
                    this.heroSliderCircleIndicators,
                ];
                // move active indictor class to correct one
                for (const sliderIndicators of allTypeSliderIndicators) {
                    if (sliderIndicators.length &&
                        (
                            child.classList.contains(CLASS_MAIN_PRODUCT_IMAGE) ||
                            child.classList.contains(CLASS_HEADER_SLIDER_CONTENT) ||
                            child.classList.contains(CLASS_CAMPAIGN_SLIDER_CONTENT)
                        )
                    ) {
                        activeItemIndex = children.indexOf(child);
                        // this kind of slider may have several sliders in one screen
                        if (child.classList.contains(CLASS_CAMPAIGN_SLIDER_CONTENT)) {
                            activeItemIndex = Math.floor(
                                activeItemIndex / this.slidersPerView
                            );
                        }
                        for (const indicator of sliderIndicators) {
                            if (indicator.classList.contains(CLASS_ACTIVE)) {
                                indicator.classList.remove(CLASS_ACTIVE);
                            }
                        }
                        sliderIndicators[activeItemIndex].classList.add(CLASS_ACTIVE);
                    }
                }
            }
        }
    }

    protected scrollToElement(element: HTMLElement): void {
        let scrollLeft: number;

        if (this.direction === DIRECTION_RIGHT) {
            scrollLeft = Math.min(element.offsetLeft, this.maxLeft);
        } else {
            scrollLeft = element.offsetLeft;
        }
        this.scrollTo(scrollLeft);
    }

    protected scrollToRight(): void {
        // hero-carousel component right button logic
        if (this.heroSliderCircleIndicators.length > 0) {
            let activeIdx = this.heroSliderCircleIndicators
                .findIndex(((val) => val.classList.contains(CLASS_ACTIVE)));
            const maxIdx = Math.ceil(this.scrollable.children.length / this.slidersPerView) - 1;
            if (activeIdx + 1 <= maxIdx) {
                this.moveToSlide(activeIdx + 1);
            }

            return;
        }
        const currentLeft: number = this.scrollable.scrollLeft;
        const children: Array<HTMLElement> = [].concat.apply([], this.scrollable.children);

        for (const child of children) {
            if (child.offsetLeft - currentLeft > 1) {
                this.scrollToElement(child);

                return;
            }
        }
    }

    protected scrollToLeft(): void {
        // hero-carousel component left button logic
        if (this.heroSliderCircleIndicators.length > 0) {
            const activeIdx = this.heroSliderCircleIndicators
                .findIndex(((val) => val.classList.contains(CLASS_ACTIVE)));
            if (activeIdx - 1 >= 0) {
                this.moveToSlide(activeIdx - 1);
            }

            return;
        }
        const currentLeft: number = this.scrollable.scrollLeft;
        const children: Array<HTMLElement> = [].concat.apply([], this.scrollable.children);
        let lastChild;
        for (const child of children) {
            if (child.offsetLeft < currentLeft) {
                lastChild = child;
                continue;
            }

            if (lastChild) {
                this.scrollToElement(lastChild);
            }

            return;
        }
    }

    protected autoNext(): void {
        if (!this.autoEnabled) {
            return;
        }

        if (this.scrollable.scrollLeft >= this.maxLeft) {
            this.scrollTo(0);
        } else {
            this.scrollToRight();
        }
    }

    protected stopAnimation(): void {
        if (this.tweezer) {
            this.tweezer.stop();
            this.tweezer = null;
        }
    }

    protected onScroll(): void {
        let { lastScrollLeft, scrollable } = this;

        this.stopAnimation();

        if (lastScrollLeft && lastScrollLeft < scrollable.scrollLeft) {
            this.direction = DIRECTION_RIGHT;
        }

        if (lastScrollLeft && lastScrollLeft > scrollable.scrollLeft) {
            this.direction = DIRECTION_LEFT;
        }

        this.lastScrollLeft = scrollable.scrollLeft;

        window.clearTimeout(this.timeout);
        this.timeout = window.setTimeout(this.onAfterScroll, 100);
    }

    protected onAfterScroll(): void {
        if (this.touchStarted) {
            return;
        }

        switch (this.direction) {
            case DIRECTION_RIGHT:
                this.scrollToRight();
                break;
            case DIRECTION_LEFT:
                this.scrollToLeft();
                break;
            default:
                break;
        }
    }

    protected onTouchStart(e: TouchEvent): void {
        this.stopAnimation();

        const { screenX } = e.touches[0];
        this.touchStarted = true;
        this.touchStartX = screenX;
        this.scrollableStartLeft = this.scrollable.scrollLeft;
        document.addEventListener(
            'touchmove',
            this.onTouchMove,
            supportsPassiveEvents ? { passive: true } : false
        );
        document.addEventListener(
            'touchend',
            this.onTouchEnd,
            supportsPassiveEvents ? { passive: true } : false
        );
    }

    protected onTouchMove(e: TouchEvent): void {
        const { screenX } = e.touches[0];
        this.scrollable.scrollLeft = this.scrollableStartLeft + this.touchStartX - screenX;
    }

    protected onTouchEnd(e: TouchEvent): void {
        this.touchStarted = false;
        const target: HTMLElement = <HTMLElement> e.target;

        document.removeEventListener('touchmove', this.onTouchMove);
        document.removeEventListener('touchend', this.onTouchEnd);

        if (closest(target, SELECTOR_SHOPPING_LIST_WIDGET) ||
            closest(target, SELECTOR_ADD_TO_CART_WIDGET) ||
            this.preventTapToSlide) {
            return;
        }
        this.onAfterScroll();
    }

    protected onMouseEnter(): void {
        this.autoEnabled = false;
    }

    protected onMouseLeave(): void {
        if (!this.hadInteractionWithSlider) {
            this.autoEnabled = true;
        }
    }

    protected stopAutoOnDesktop(): void {
        clearInterval(this.autoDesktopTimeout);
    }

    protected startAutoOnDesktop(): void {
        if (this.autoDesktopTime > 0 && document.body.offsetWidth > SCREEN_LARGE.minWidth) {
            this.autoDesktopTimeout = window.setInterval(
                this.autoNext,
                this.autoDesktopTime
            );
        }
    }

    protected setInteractionState(): void {
        this.hadInteractionWithSlider = true;
        this.autoEnabled = false;
    }

    protected onButtonLeftClick(e: MouseEvent) {
        e.preventDefault();

        this.autoEnabled = false;
        this.scrollToLeft();
    }

    protected onButtonRightClick(e: MouseEvent) {
        e.preventDefault();

        this.autoEnabled = false;
        this.scrollToRight();
    }

    protected hasSliderContent(scrollable: HTMLElement): boolean {
        return scrollable.hasChildNodes();
    }

    protected initSwipeGensture = () => {
        let initalPosition = 0;
        let currentPosition = 0;
        let locked = false;
        let allowClick = true;
        let initialDistance = 0;
        const unify = (event: TouchEvent | PointerEvent) => {
            if ('touches' in event) {
                return event?.changedTouches?.[0];
            }

            return event;
        };
        const lock = (event: TouchEvent | PointerEvent) => {
            this.scrollable.classList.remove('smooth');
            initalPosition = unify(event).clientX;
            currentPosition = 0;
            initialDistance = this.getElementTranslateX(this.scrollable);
            locked = true;
            allowClick = false;

            return;
        };
        const move = (event: TouchEvent | PointerEvent) => {
            if (locked) {
                currentPosition = unify(event).clientX;
                this.scrollable.style.transform = `translateX(${(initialDistance + currentPosition - initalPosition)}px)`;
            }

            return;
        };
        const drag = () => {
            const distance = currentPosition - initalPosition;
            allowClick = Math.abs(distance) < 5 || currentPosition === 0;
            if (locked && !allowClick) {
                const maxWidth = this.slideMoveMaxWidth;

                let end = 0;
                // swipe left
                if (distance <= 0) {
                    end = initialDistance - this.slideWidth;
                } else {
                    end = initialDistance + this.slideWidth;
                }
                if (end > 0) {
                    end = 0;
                }
                if (end < -maxWidth) {
                    end = -maxWidth;
                }
                this.moveToSlide(Math.ceil(Math.abs(end / this.slideWidth)));
                initialDistance = end;
            }
            locked = false;

            return;
        };

        // check if its mobile, use touch event, otherwise use pointer event
        if ('ontouchstart' in window) {
            this.scrollable.addEventListener('touchstart', lock);
            this.scrollable.addEventListener('touchmove', move);
            this.scrollable.addEventListener('touchend', drag);
            this.scrollable.addEventListener('touchcancel', drag);
        } else {
            this.scrollable.addEventListener('pointerdown', lock);
            this.scrollable.addEventListener('pointermove', move);
            this.scrollable.addEventListener('pointerup', drag);
            this.scrollable.addEventListener('pointerleave', drag);
        }
        this.scrollable.addEventListener('click', (event: MouseEvent) => {
            if (!allowClick) {
                event.preventDefault();
            }
        });
    }

    protected getElementTranslateX = (ele: HTMLElement) => {
        const style = window.getComputedStyle(ele);
        const matrix = new DOMMatrixReadOnly(style.transform);

        return matrix.m41;
    }

    protected moveToSlide = (idx: number) => {
        // add animation class
        if (!this.scrollable.classList.contains('smooth')) {
            this.scrollable.classList.add('smooth');
        }

        let moveWidth = this.slideWidth * idx;
        if (moveWidth > this.slideMoveMaxWidth) {
            moveWidth = this.slideMoveMaxWidth;
        }

        this.scrollable.style.transform = `translateX(${-moveWidth}px)`;

        // update dot active classes
        const allTypeSliderIndicators = [
            this.heroSliderTextIndicators,
            this.heroSliderCircleIndicators,
        ];
        // move active indictor class to correct one
        for (const sliderIndicators of allTypeSliderIndicators) {
            for (const indicator of sliderIndicators) {
                indicator.classList.remove(CLASS_ACTIVE);
            }
            sliderIndicators[idx]?.classList.add(CLASS_ACTIVE);
        }

        // check if right/left buttons need to be disabled
        if (this.buttonLeft) {
            this.buttonLeft.disabled = idx === 0;
        }

        if (this.buttonRight) {
            this.buttonRight.disabled = moveWidth === this.slideMoveMaxWidth;
        }
    }
}
